import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Loader2, ScrollText, RefreshCw } from "lucide-react";
import { Button } from "../../components/ui/button";
import ExportButtons from "../../components/ExportButtons";
import { stampedName } from "../../lib/csv";

const ACTION_LABEL = {
  "transaction.add": { label: "Added transaction", color: "emerald" },
  "transaction.edit": { label: "Edited transaction", color: "amber" },
  "transaction.delete": { label: "Deleted transaction", color: "rose" },
  "transaction.bulk_upload": { label: "Bulk uploaded transactions", color: "blue" },
  "member.add": { label: "Added member", color: "emerald" },
  "member.edit": { label: "Edited member", color: "amber" },
  "member.delete": { label: "Removed member", color: "rose" },
  "gmail.add": { label: "Granted Gmail access", color: "emerald" },
  "gmail.edit": { label: "Edited Gmail access", color: "amber" },
  "gmail.revoke": { label: "Revoked Gmail access", color: "rose" },
  "portfolio_values.update": { label: "Updated portfolio values", color: "blue" },
  "holdings.upload": { label: "Uploaded holdings CSV", color: "blue" },
};

const COLOR_CLASSES = {
  emerald: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  amber: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  rose: "bg-rose-500/10 text-rose-300 border-rose-500/30",
  blue: "bg-blue-500/10 text-blue-300 border-blue-500/30",
  slate: "bg-slate-500/10 text-slate-300 border-slate-500/30",
};

function formatTs(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function formatMeta(meta) {
  if (!meta || Object.keys(meta).length === 0) return null;
  // Compact summary based on common keys
  const parts = [];
  if (meta.member) parts.push(`${meta.member}`);
  if (meta.name) parts.push(`${meta.name}`);
  if (meta.type) parts.push(meta.type);
  if (meta.funds !== undefined) parts.push(`₹${Number(meta.funds).toLocaleString("en-IN")}`);
  if (meta.inserted !== undefined) parts.push(`${meta.inserted} rows`);
  if (meta.count !== undefined) parts.push(`${meta.count} rows`);
  if (meta.role) parts.push(`role: ${meta.role}`);
  if (meta.statementDate) parts.push(`stmt: ${meta.statementDate}`);
  if (parts.length === 0 && meta.changes) {
    const changes = meta.changes;
    return Object.keys(changes).slice(0, 3).map((k) => `${k}=${changes[k]}`).join(", ");
  }
  return parts.join(" · ");
}

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.auditLog(300);
      setRows(r);
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const exportData = useMemo(() => {
    const headers = ["When", "Admin Email", "Admin Name", "Action", "Target Type", "Target ID", "Details"];
    const csvRows = rows.map((r) => [
      r.ts, r.actorEmail || "", r.actorName || "", r.action,
      r.targetType || "", r.targetId || "", formatMeta(r.meta) || "",
    ]);
    const pdfColumns = ["When", "Admin", "Action", "Details"];
    const pdfRows = rows.map((r) => {
      const a = ACTION_LABEL[r.action] || { label: r.action };
      return [
        formatTs(r.ts),
        r.actorEmail || r.actorName || "—",
        a.label,
        formatMeta(r.meta) || "—",
      ];
    });
    return { headers, csvRows, pdfColumns, pdfRows };
  }, [rows]);

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <ScrollText size={16} className="text-amber-400" />
            <div className="text-sm font-semibold text-white">Audit Log</div>
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Every admin action is recorded here for accountability.
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ExportButtons
            testIdPrefix="audit-export"
            adminOnly
            disabled={rows.length === 0}
            csv={{
              filename: stampedName("audit_log"),
              headers: exportData.headers,
              rows: exportData.csvRows,
            }}
            pdf={{
              title: "Audit log",
              subtitle: `${rows.length} entries`,
              filename: "audit_log",
              orientation: "landscape",
              columns: exportData.pdfColumns,
              rows: exportData.pdfRows,
            }}
          />
          <Button
            onClick={load}
            disabled={loading}
            data-testid="audit-refresh"
            className="bg-slate-800 hover:bg-slate-700 text-white border border-slate-700 h-9"
          >
            <RefreshCw size={12} className={`mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      <div className="mt-5 bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400">
            <Loader2 className="animate-spin mr-2" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-slate-500 py-12 text-center">No admin actions recorded yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-900/80 text-[11px] text-slate-400 uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">When</th>
                  <th className="text-left px-4 py-3">Admin</th>
                  <th className="text-left px-4 py-3">Action</th>
                  <th className="text-left px-4 py-3">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {rows.map((r) => {
                  const a = ACTION_LABEL[r.action] || { label: r.action, color: "slate" };
                  const meta = formatMeta(r.meta);
                  return (
                    <tr key={r.id} className="hover:bg-slate-800/40">
                      <td className="px-4 py-3 text-slate-300 whitespace-nowrap text-xs">
                        {formatTs(r.ts)}
                      </td>
                      <td className="px-4 py-3 text-slate-300 text-xs max-w-[200px] truncate">
                        {r.actorName || r.actorEmail || "—"}
                        {r.actorEmail && r.actorName && (
                          <div className="text-[10px] text-slate-500 truncate">{r.actorEmail}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] px-2 py-0.5 rounded border whitespace-nowrap ${COLOR_CLASSES[a.color]}`}>
                          {a.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-400 text-xs">{meta || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
